import { useVirtualizer } from "@tanstack/react-virtual";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { Row, TableBodyPropGetter, TableBodyProps } from "react-table";
import { BaseButton } from "../BaseButton";
import { getColumnAlignClassName } from "./column-align";

/* .base-table__td 높이. 실제 높이는 measureElement 로 다시 잼 */
export const TABLE_ROW_HEIGHT = 70;

type Props<D extends object> = {
  rows: Array<Row<D>>;
  prepareRow: (row: Row<D>) => void;
  getTableBodyProps: (propGetter?: TableBodyPropGetter<D>) => TableBodyProps;
  scrollElementRef: RefObject<HTMLElement>;
  tableWidth: number;
  getCellClassName?: (row: Row<D>) => string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoadError?: boolean;
  onRetry?: () => void;
};

export const VirtualTableBody = <D extends object>({
  rows,
  prepareRow,
  getTableBodyProps,
  scrollElementRef,
  tableWidth,
  getCellClassName,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isLoadError,
  onRetry,
}: Props<D>) => {
  const getItemKey = useCallback((index: number) => rows[index]?.id ?? index, [rows]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => TABLE_ROW_HEIGHT,
    getItemKey,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const lastRenderedIndex = virtualRows.length > 0 ? virtualRows[virtualRows.length - 1].index : -1;

  /* 부모가 매 렌더 새 콜백을 넘겨도 effect 가 다시 돌지 않도록 ref 로 보관 */
  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  });

  const canLoadMore = Boolean(onLoadMore);

  useEffect(() => {
    /* isLoadError 가 없으면 실패 직후 다시 요청해 무한 반복됨 */
    if (!canLoadMore || !hasMore || isLoadingMore || isLoadError) return;
    /* overscan 범위까지 렌더되므로 화면 끝보다 10행 먼저 요청됨 */
    if (lastRenderedIndex >= rows.length - 1) {
      onLoadMoreRef.current?.();
    }
  }, [lastRenderedIndex, rows.length, canLoadMore, hasMore, isLoadingMore, isLoadError]);

  const loadErrorContent = (
    <>
      <span className="text-red">목록을 불러오지 못했습니다.</span>
      {onRetry && <BaseButton title="다시 시도" className="size-small color-white no-outline text-primary3 ml10" onClick={onRetry} />}
    </>
  );

  if (rows.length === 0) {
    return (
      <div {...getTableBodyProps()} className="body">
        <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
          <div className="base-table__td w-100 text-center">
            <div className="w-100 table-no-data">
              {isLoadError ? loadErrorContent : <p>{isLoadingMore ? "불러오는 중..." : "데이터가 없습니다."}</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bodyProps = getTableBodyProps();

  /* absolute + transform 을 쓰면 useSticky 고정 컬럼(position: sticky)이 깨지므로 padding 으로 채움 */
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom = Math.max(0, rowVirtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1]?.end ?? 0));

  return (
    <>
      <div {...bodyProps} className="body" style={{ ...bodyProps.style, paddingTop, paddingBottom, width: tableWidth }}>
        {virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index];
          prepareRow(row);
          const { key, ...rowProps } = row.getRowProps();
          const cellClassName = getCellClassName ? getCellClassName(row) : "";

          return (
            /* data-index 가 없으면 measureElement 가 측정을 건너뜀 */
            <div key={key} {...rowProps} data-index={virtualRow.index} ref={rowVirtualizer.measureElement} className="base-table__tr">
              {row.cells.map((cell) => {
                const { key: cellKey, ...cellProps } = cell.getCellProps();
                return (
                  <div
                    key={cellKey}
                    {...cellProps}
                    className={`base-table__td ${cellClassName} ${getColumnAlignClassName(cell.column as { align?: string })}`}
                  >
                    {cell.render("Cell")}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {onLoadMore && (isLoadError || isLoadingMore || !hasMore) && (
        <div className="base-table__list-status" style={{ width: tableWidth }}>
          {isLoadError ? loadErrorContent : <span>{isLoadingMore ? "불러오는 중..." : "마지막 항목입니다."}</span>}
        </div>
      )}
    </>
  );
};
